import React, { useState } from 'react';
import { Card, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { subtleBadgeProps } from '@core/utils/subtleBadge';
import type {
  DashboardChildBucket,
  DashboardReportingProgressPeriod,
} from '../types/dashboard';
import { departmentProgressPath } from '../utils/dashboardPaths';
import PreviewNameList from '@core/reports/components/PreviewNameList';
import {
  formatDeadlineCountdown,
  formatPeriodRangeLabel,
  formatReportTagDeadline,
} from '@features/report/tag/utils/reportTagDeadline';

interface Props {
  periods: DashboardReportingProgressPeriod[];
  scope: string;
  scopeId: number;
}

function progressBadgeVariant(
  status: string,
  percent: number
): 'success' | 'warning' | 'danger' | 'secondary' {
  if (status === 'complete' || percent >= 100) return 'success';
  if (status === 'not_started' || percent === 0) return 'danger';
  return 'warning';
}

function periodStatusLabel(status: string, percent: number): string {
  if (status === 'complete' || percent >= 100) {
    return 'Complete';
  }
  if (status === 'not_started' || percent === 0) {
    return 'Not started';
  }
  return 'In progress';
}

function ChildBucketBlock({
  label,
  bucket,
}: {
  label: string;
  bucket: DashboardChildBucket;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mb-3 px-3">
      <button
        type="button"
        className="btn btn-link btn-sm px-0 text-decoration-none"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {label}: {bucket.approved} approved · {bucket.outstanding} outstanding{' '}
        {expanded ? <FiChevronUp /> : <FiChevronDown />}
      </button>
      {expanded ? (
        <div className="ps-2 border-start border-secondary-subtle">
          <div className="fw-semibold small mt-1">Outstanding ({bucket.outstanding})</div>
          <PreviewNameList items={bucket.outstanding_items} emptyLabel="None outstanding" />
          <div className="fw-semibold small mt-2">Approved ({bucket.approved})</div>
          <PreviewNameList items={bucket.approved_items} emptyLabel="None approved yet" />
        </div>
      ) : null}
    </div>
  );
}

const ReportingProgressPanel: React.FC<Props> = ({ periods, scope, scopeId }) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  if (!periods.length) return null;

  const formatDepartmentName = (name: string, isRequired?: boolean) =>
    isRequired ? `${name} *` : name;

  const hasOfficeProgress = periods.some((p) => p.office_progress);
  const hasChildrenProgress = periods.some((p) => p.children_progress);
  const hasDualProgress = hasOfficeProgress && hasChildrenProgress;

  const renderDepartmentsList = (
    departments: typeof periods[0]['departments'],
    reportTagId: number
  ) => (
    <div className="dashboard-progress-period__details">
      {departments?.map((dept) => {
        const canLink = dept.status !== 'NOT_STARTED';
        const path = canLink
          ? departmentProgressPath(
              scope,
              scopeId,
              reportTagId,
              dept.department_id
            )
          : null;

        return (
          <div
            key={dept.department_id}
            className="dashboard-progress-dept-row d-flex justify-content-between align-items-center gap-3"
          >
            <div className="min-w-0">
              {path ? (
                <Link
                  to={path}
                  className="fw-medium text-truncate d-block text-decoration-none"
                >
                  {formatDepartmentName(dept.department_name, dept.is_required)}
                </Link>
              ) : (
                <div className="fw-medium text-truncate">
                  {formatDepartmentName(dept.department_name, dept.is_required)}
                </div>
              )}
              <small className="text-muted">{dept.status_label}</small>
            </div>
            <div className="text-end flex-shrink-0">
              <div className="fw-semibold">{dept.department_percent}%</div>
              <small className="text-muted">
                +{dept.period_contribution_percent}% toward period
              </small>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderEntitiesList = (entities: typeof periods[0]['entities']) => (
    <div className="dashboard-progress-period__details">
      {entities?.map((entity) => (
        <div
          key={entity.church_id ?? entity.district_id}
          className="dashboard-progress-dept-row d-flex justify-content-between align-items-center gap-3"
        >
          <div className="min-w-0">
            <div className="fw-medium text-truncate">
              {entity.church_name ?? entity.district_name}
            </div>
            <small className="text-muted">{entity.status_label}</small>
          </div>
          <div className="text-end flex-shrink-0">
            <div className="fw-semibold">{entity.reporting_progress_percent}%</div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Card className="ntcog-card dashboard-section-card mb-4">
      <Card.Header>
        <div>Required reporting progress</div>
        <small className="text-muted fw-normal d-block">
          {hasDualProgress
            ? 'Shows office and children progress separately. Office = your direct submissions, Children = submissions from churches/districts below you.'
            : 'Shows required departments and any department reports submitted for each period. Each listed department contributes equally. Approved = 100% for that department.'}
        </small>
        <small className="text-muted fw-normal d-block mt-1">* Required for this reporting period</small>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="dashboard-progress-list">
          {periods.map((period) => {
            const percent = period.summary.reporting_progress_percent ?? 0;
            const isExpanded = expandedId === period.report_tag_id;
            const statusLabel = periodStatusLabel(period.status, percent);

            const officePercent = period.office_progress?.reporting_progress_percent ?? 0;
            const childrenPercent = period.children_progress?.reporting_progress_percent ?? 0;
            const periodLabel = formatPeriodRangeLabel(period.since, period.until);
            const deadlineLabel = formatReportTagDeadline(period.deadline);
            const periodComplete = period.status === 'complete' || percent >= 100;
            const countdown = periodComplete
              ? null
              : formatDeadlineCountdown(period.deadline);

            return (
              <div key={period.report_tag_id} className="dashboard-progress-period">
                <button
                  type="button"
                  className="dashboard-progress-period__toggle w-100 border-0 bg-transparent text-start"
                  onClick={() =>
                    setExpandedId(isExpanded ? null : period.report_tag_id)
                  }
                  aria-expanded={isExpanded}
                >
                  <div className="dashboard-progress-period__row d-flex align-items-center justify-content-between gap-3">
                    <div className="min-w-0">
                      <div className="fw-semibold">{period.period_name}</div>
                      <small className="text-muted">
                        {statusLabel} · {percent}% reporting progress
                        {hasDualProgress && (
                          <> (Office: {officePercent}%, Children: {childrenPercent}%)</>
                        )}
                      </small>
                      {(periodLabel || deadlineLabel) && (
                        <small className="text-muted d-block">
                          {periodLabel ? `Period: ${periodLabel}` : null}
                          {periodLabel && deadlineLabel ? ' · ' : null}
                          {deadlineLabel ? `Deadline: ${deadlineLabel}` : null}
                          {countdown ? ` · ${countdown}` : null}
                        </small>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                      <Badge {...subtleBadgeProps(progressBadgeVariant(period.status, percent))}>
                        {percent}%
                      </Badge>
                      {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                    </div>
                  </div>
                </button>

                {isExpanded && hasDualProgress && (
                  <div className="dashboard-progress-period__details">
                    {period.office_progress && (
                      <div className="mb-3">
                        <div className="fw-semibold text-muted small mb-2 px-3 pt-2">
                          Office Progress ({officePercent}%)
                        </div>
                        {renderDepartmentsList(period.office_progress.departments, period.report_tag_id)}
                      </div>
                    )}
                    {period.children_progress && (
                      <div>
                        <div className="fw-semibold text-muted small mb-2 px-3 border-top pt-2">
                          Children Progress ({childrenPercent}%)
                        </div>
                        {period.children_progress.churches ? (
                          <ChildBucketBlock
                            label="Churches"
                            bucket={period.children_progress.churches}
                          />
                        ) : null}
                        {period.children_progress.districts ? (
                          <ChildBucketBlock
                            label="Districts"
                            bucket={period.children_progress.districts}
                          />
                        ) : null}
                        {!period.children_progress.churches &&
                        !period.children_progress.districts
                          ? renderEntitiesList(period.children_progress.entities)
                          : null}
                      </div>
                    )}
                  </div>
                )}

                {isExpanded && !hasDualProgress && (
                  renderDepartmentsList(period.departments, period.report_tag_id)
                )}
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ReportingProgressPanel;
